import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { IonButton, IonIcon, IonInput, IonItem } from '@ionic/react';
import { alertCircle, calendarOutline, locationOutline, search } from 'ionicons/icons';
import moment from 'moment';
import Loading from '../../components/spinner';
import { withTranslation } from '../../lib/translate';
import Layout from '../../components/layout';
import { NormalText, SmallText, Spacer, StrongText, Title } from '../../components/common';
import NoData from '../../components/noData';
import Basket from '../../lib/basket';
import { forwardTo } from '../../lib/utils';
import { getEvents } from '../../store/actions';
import './index.css';

const ALL_CATEGORIES = 'all';

class Events extends React.Component {
  state = {
    searchValue: '',
    selectedCategory: ALL_CATEGORIES,
  };

  listRef = React.createRef();

  componentDidMount() {
    this.props.dispatch(getEvents());
  }

  componentDidUpdate(prevProps) {
    if (prevProps.events !== this.props.events) {
      this.loadingMore = false;
      this.loadMoreIfNotScrollable();
    }
  }

  loadMoreIfNotScrollable = () => {
    const el = this.listRef.current;
    if (el && el.scrollHeight <= el.clientHeight) {
      this.loadMore();
    }
  };

  loadMore = () => {
    const { eventsHasMore, eventsPage } = this.props;
    if (!eventsHasMore || this.loadingMore) {
      return;
    }
    this.loadingMore = true;
    this.props.dispatch(getEvents({}, (eventsPage || 1) + 1));
  };

  onScroll = (e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight <= 100) {
      this.loadMore();
    }
  };

  getCategories = () => {
    const categories = [];
    (this.props.events || []).forEach((event) => {
      const { category } = event;
      if (category && !categories.find((cat) => cat.id === category.id)) {
        categories.push(category);
      }
    });
    return categories.sort((a, b) => a.sort_order - b.sort_order);
  };

  getFilteredEvents = () => {
    const { searchValue, selectedCategory } = this.state;
    const searchTerm = searchValue.trim().toLowerCase();
    return (this.props.events || []).filter((event) => {
      const matchesSearch = event.name.toLowerCase().includes(searchTerm);
      const matchesCategory =
        selectedCategory === ALL_CATEGORIES || event.category?.id === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  };

  getEventImage = (event) => {
    return event.primary_image || null;
  };

  getStartingPrice = (event) => {
    const prices = (event.ticket_tiers || [])
      .filter((tier) => tier.is_active)
      .map((tier) => tier.price);
    if (prices.length > 0) {
      return Math.min(...prices) / 100;
    }
    return event.price_from != null ? event.price_from / 100 : null;
  };

  viewEvent = (event) => forwardTo('/event-details', { id: event.id });

  renderNoEvents = (title, text) => {
    const { __ } = this.props;
    return (
      <NoData className="no-events-page">
        <IonIcon icon={calendarOutline} />
        <Spacer size={1} />
        <Title>{__(title)}</Title>
        <Spacer size={1} />
        <NormalText>{__(text)}</NormalText>
      </NoData>
    );
  };

  render() {
    const { __ } = this.props;
    const { searchValue, selectedCategory } = this.state;
    const categories = this.getCategories();
    const filteredEvents = this.getFilteredEvents();
    const hasEvents = (this.props.events || []).length > 0;

    return (
      <Loading transparent>
        <Layout showHamburger title={__('Events')} headerWithTitle color="transparent" >
          <div className="absolute-content flex-row-wrapper">
            {this.props?.location?.state?.error && (
              <>
                <div className="events-error-wrapper">
                  <IonIcon color="danger" icon={alertCircle} />
                  <NormalText className="events-error-text">
                    {__(this.props.location.state.error)}
                  </NormalText>
                </div>
                <Spacer size={2} />
              </>
            )}
            <Title className={'web-only'}>{__('Events')}</Title>
            <div style={{ flex: 'unset' }}>
              {hasEvents && (
                <>
                  <IonItem className="input-field-wrapper" lines="none">
                    <IonIcon icon={search} slot="start" />
                    <IonInput
                      onIonInput={(e) => this.setState({ searchValue: e.target.value })}
                      value={searchValue}
                      type="text"
                      pattern="text"
                      inputmode="text"
                      placeholder={__('Search events')}
                      clearInput
                    />
                  </IonItem>
                  <Spacer size={2} />
                  <div className="events-categories">
                    <div
                      className={`events-category-pill${selectedCategory === ALL_CATEGORIES ? ' events-category-pill-selected' : ''
                        }`}
                      onClick={() => this.setState({ selectedCategory: ALL_CATEGORIES })}
                    >
                      {__('All')}
                    </div>
                    {categories.map((category) => (
                      <div
                        key={`event-category-${category.id}`}
                        className={`events-category-pill${selectedCategory === category.id ? ' events-category-pill-selected' : ''
                          }`}
                        onClick={() => this.setState({ selectedCategory: category.id })}
                      >
                        {__(category.name)}
                      </div>
                    ))}
                  </div>
                  <Spacer size={2} />
                </>
              )}

            </div>
            <div className="events-list scrollable-y" ref={this.listRef} onScroll={this.onScroll}>
              {filteredEvents.length === 0 ? (
                hasEvents ? (
                  this.renderNoEvents(
                    'No events found',
                    'Try a different search or category',
                  )
                ) : (
                  this.renderNoEvents(
                    'No events yet',
                    'New events will appear here as soon as they are announced',
                  )
                )
              ) : (
                filteredEvents.map((event) => {
                  const image = this.getEventImage(event);
                  const startingPrice = this.getStartingPrice(event);
                  const startTime = moment(event.start_time);
                  return (
                    <div
                      className="events-list-item box-wrapper"
                      key={`event-${event.id}`}
                      onClick={() => this.viewEvent(event)}
                    >
                      {image && <img src={image} alt={event.name} />}
                      {event.category && (
                        <div className="events-list-item-category">
                          <SmallText>{__(event.category.name)}</SmallText>
                        </div>
                      )}
                      <div className="events-list-item-data">
                        <StrongText className="block">{event.name}</StrongText>
                        <div className="events-list-item-detail">
                          <IonIcon icon={calendarOutline} />
                          <SmallText>{`${startTime.format('LL')} · ${startTime.format('h:mm A')}`}</SmallText>
                        </div>
                        <div className="events-list-item-detail">
                          <IonIcon icon={locationOutline} />
                          <SmallText>{event.venue_name}</SmallText>
                        </div>
                        <Spacer size={1} />
                        <div className="events-list-item-footer">
                          <div>
                            {startingPrice !== null && (
                              <>
                                <SmallText className="block light-color">{__('Starting from')}</SmallText>
                                <StrongText className="block">
                                  {Basket.formatPrice(startingPrice, true)}
                                </StrongText>
                              </>
                            )}
                          </div>
                          <IonButton className="button-uppercase" size="small">
                            {__('View details')}
                          </IonButton>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </Layout>
      </Loading>
    );
  }
}

const stateToProps = (store) => {
  const { events, eventsPage, eventsHasMore } = store.orders;
  return {
    events,
    eventsPage,
    eventsHasMore,
  };
};

export default connect(stateToProps)(withRouter(withTranslation(Events)));
